import React, { type ComponentType, type ReactNode } from 'react';
import { keyframes, styled } from '@compiled/react';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type {
	EditorProps,
	WithEditorActions as WithEditorActionsType,
} from '@atlaskit/editor-core';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error.tsx';
import { ScrollableParentConsumer } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/scrollable-parent-context/index.tsx';
import { Consumer as FieldHeightAnimatorConsumer } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/height-animator-context/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { PACKAGE_NAME } from '../../../../common/constants.tsx';
import { AsyncEditorView } from './main.tsx';
import messages from './messages.tsx';

export type Props = EditorProps & {
	readonly onUpdate: (adf: ADFEntity) => void;
};

const ERROR_ID = 'importEditor';

export const LoadingComponent = () => (
	<>
		<Skeleton width="44%" height="42px" />
		<Spacer height="22px" />
		<Skeleton width="90%" height="20px" />
		<Spacer height="16px" />
		<Skeleton width="63%" height="14px" />
		<Spacer height="24px" />
	</>
);

export const ErrorComponent = () => {
	const intl = useIntl();
	return (
		<Box xcss={errorWrapperStyles}>
			<SectionMessage appearance="error">
				{intl.formatMessage(messages.editorFailedToLoad)}
			</SectionMessage>
		</Box>
	);
};

const renderAsyncEditor = ({
	EditorContext,
	WithEditorActions,
	Editor,
	props,
	scrollableElement,
}: {
	EditorContext: ComponentType<{
		children: ReactNode;
	}>;
	WithEditorActions: typeof WithEditorActionsType;
	Editor: ComponentType<EditorProps>;
	props: Props;
	scrollableElement: HTMLElement | null;
}) => (
	<FieldHeightAnimatorConsumer>
		{(onRecalculateAnimatorHeight) => (
			<AsyncEditorView
				EditorContext={EditorContext}
				WithEditorActions={WithEditorActions}
				Editor={Editor}
				onRecalculateAnimatorHeight={onRecalculateAnimatorHeight}
				scrollableElement={scrollableElement}
				{...props}
			/>
		)}
	</FieldHeightAnimatorConsumer>
);

const importEditorCore = () =>
	famaCatchAndLogFetchError(
		import(/* webpackChunkName: "async-@atlaskit/editor-core" */ '@atlaskit/editor-core'),
		PACKAGE_NAME,
		toErrorId(ERROR_ID),
		toErrorMessage('Failed to async load the Atlaskit editor for multiline configuration'),
	);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyEditorRenderer = lazy(() =>
	importEditorCore().then(({ EditorContext, WithEditorActions, Editor }) => (props: Props) => (
		<ScrollableParentConsumer>
			{(scrollableParent) =>
				renderAsyncEditor({
					// @ts-expect-error - TS2322 - Type 'typeof LegacyEditorContext' is not assignable to type 'ComponentType<{ children: ReactNode; }>'.
					EditorContext,
					WithEditorActions,
					Editor,
					props,
					scrollableElement: scrollableParent.current,
				})
			}
		</ScrollableParentConsumer>
	)),
);

export const AsyncEditor = (props: Props) => (
	<JSErrorBoundary
		id={ERROR_ID}
		packageName={PACKAGE_NAME}
		teamName="jsd-shield"
		fallback={ErrorComponent}
	>
		<Placeholder name="async-editor" fallback={<LoadingComponent />}>
			<LazyEditorRenderer {...props} />
		</Placeholder>
	</JSErrorBoundary>
);

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1000px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Skeleton = styled.div<{
	children?: ReactNode;
	height: string;
	width: string;
}>({
	animationDuration: '1s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationName: shimmer,
	animationTimingFunction: 'linear',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral'),
	backgroundImage: `linear-gradient( to right, ${token('color.background.neutral')} 10%, ${token('color.background.neutral')} 30%, ${token('color.background.neutral')} 50% )`,
	backgroundRepeat: 'no-repeat',
	borderRadius: '2px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => props.height,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props) => props.width,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacer = styled.div<{
	children?: ReactNode;
	height: string;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => props.height,
});

const errorWrapperStyles = xcss({
	marginBottom: 'space.200',
});
